"use client"

import * as React from "react"
import { Label } from "@/components/ui/label"
import { useQueryClient } from '@tanstack/react-query'
import { MessageCircle, CheckCircle2, XCircle, Loader2, AlertTriangle, Plus, RefreshCw } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { whatsappService, WhatsAppServiceError } from "@/services/whatsappService"
import type { WhatsAppConnectionStatus } from "@/services/whatsappService"
import { facebookOAuthService, FacebookOAuthError } from "@/services/facebookOAuthService"
import { conversationService } from "@/services/ConversationService"
import {
  CONVERSATIONS_API_URL,
  WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID,
  WHATSAPP_USE_EMBEDDED_SIGNUP,
  getConversationsAuthToken,
} from "@/config/env"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { notifyChannelConnectionChanged } from "@/lib/channel-connection-events"
import { queryKeys } from '@/queries'

function isMissingPermissionRegistrationError(error?: string | null): boolean {
  if (!error) return false
  const normalized = error.toLowerCase()
  return normalized.includes("missing permission") || normalized.includes("(#100)")
}

interface WhatsAppConnectionProps {
  translations?: Record<string, unknown>
}

export function WhatsAppConnection({ translations }: WhatsAppConnectionProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [connectionStatus, setConnectionStatus] = React.useState<WhatsAppConnectionStatus | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [retryLoading, setRetryLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [initialLoading, setInitialLoading] = React.useState(true)

  const getTranslation = React.useCallback((key: string): string => {
    if (translations) {
      const keys = key.split('.')
      let value: Record<string, unknown> = translations
      for (const k of keys) {
        value = value?.[k] as Record<string, unknown>
      }
      return typeof value === 'string' ? value : key
    }
    return t(key)
  }, [translations, t])

  const syncWhatsAppStatus = React.useCallback((status: WhatsAppConnectionStatus) => {
    setConnectionStatus(status)
    queryClient.setQueryData(queryKeys.meta.whatsappStatus(), status)
  }, [queryClient])

  // Load initial connection status
  React.useEffect(() => {
    const loadStatus = async () => {
      try {
        setInitialLoading(true)
        const token = getConversationsAuthToken()
        if (!token) {
          syncWhatsAppStatus({ connected: false })
          setError(null)
          return
        }
        const status = await whatsappService.getStatus()
        syncWhatsAppStatus(status)
        setError(null)
      } catch (err) {
        console.error('Error loading WhatsApp status:', err)
        setError(err instanceof WhatsAppServiceError ? err.message : getTranslation('settings.whatsapp.error_loading'))
        syncWhatsAppStatus({ connected: false })
      } finally {
        setInitialLoading(false)
      }
    }

    loadStatus()
  }, [getTranslation, syncWhatsAppStatus])

  const registrationPending =
    connectionStatus?.metadata?.phone_registration_status === "pending"
  const permissionPending =
    connectionStatus?.connected === true &&
    connectionStatus?.metadata?.phone_registered !== true &&
    (connectionStatus?.metadata?.phone_registration_status === "permission_pending" ||
      connectionStatus?.metadata?.phone_registration_blocker === "business_management_advanced_access" ||
      isMissingPermissionRegistrationError(connectionStatus?.metadata?.phone_registration_error))
  const registrationFailed =
    connectionStatus?.connected === true &&
    connectionStatus?.metadata?.phone_registered !== true &&
    (connectionStatus?.metadata?.phone_registration_status === "failed" ||
      Boolean(connectionStatus?.metadata?.phone_registration_error)) &&
    !registrationPending &&
    !permissionPending
  const webhookSubscriptionFailed =
    connectionStatus?.connected === true &&
    connectionStatus?.metadata?.webhook_subscribed !== true &&
    (connectionStatus?.metadata?.webhook_subscription_status === "failed" ||
      Boolean(connectionStatus?.metadata?.webhook_subscription_error))
  const isConnected = connectionStatus?.connected === true
  const isReady =
    isConnected &&
    connectionStatus?.metadata?.phone_registered === true &&
    connectionStatus?.metadata?.webhook_subscribed === true

  React.useEffect(() => {
    if (!isConnected || isReady || registrationFailed || permissionPending || webhookSubscriptionFailed) {
      return
    }
    let cancelled = false
    let pollCount = 0
    const maxPolls = 20
    const id = window.setInterval(async () => {
      if (cancelled) return
      pollCount += 1
      try {
        const next = await whatsappService.getStatus()
        if (cancelled) return
        syncWhatsAppStatus(next)
        const m = next.metadata
        if (
          (m?.phone_registered === true && m?.webhook_subscribed === true) ||
          m?.phone_registration_status === "failed" ||
          m?.phone_registration_status === "permission_pending" ||
          m?.webhook_subscription_status === "failed" ||
          pollCount >= maxPolls
        ) {
          window.clearInterval(id)
        }
      } catch {
        window.clearInterval(id)
      }
    }, 3000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [isConnected, isReady, registrationFailed, permissionPending, webhookSubscriptionFailed, syncWhatsAppStatus])

  const handleRetryRegistration = async () => {
    try {
      setRetryLoading(true)
      setError(null)
      await whatsappService.retryRegistration()
      const status = await whatsappService.getStatus()
      syncWhatsAppStatus(status)
    } catch (err) {
      setError(
        err instanceof WhatsAppServiceError
          ? err.message
          : getTranslation("settings.whatsapp.retry_error")
      )
    } finally {
      setRetryLoading(false)
    }
  }

  const connectWithLegacyFlow = async () => {
    const accessToken = await facebookOAuthService.login(undefined, 'token')

    if (!accessToken) {
      throw new Error(getTranslation('settings.whatsapp.error_no_token') || 'Access token is required')
    }

    // Server-side discovery via /me/businesses, client WABAs, granular scopes, etc.
    await whatsappService.connect({ access_token: accessToken })
  }

  const connectWithEmbeddedSignup = async (): Promise<WhatsAppConnectionStatus> => {
    if (!WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID) {
      throw new Error('WhatsApp Embedded Signup configuration is not configured')
    }

    const embeddedSignupResult = await facebookOAuthService.whatsappEmbeddedSignup(
      WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID
    )

    if (!embeddedSignupResult.code) {
      throw new Error(getTranslation('settings.whatsapp.error_no_code') || 'Authorization code is required')
    }

    const baseUrl = CONVERSATIONS_API_URL || ''
    const token = getConversationsAuthToken()
    const response = await fetch(`${baseUrl}/api/v1/auth/whatsapp/connect/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        code: embeddedSignupResult.code,
        redirect_uri: '',
        ...(embeddedSignupResult.phone_number_id && {
          phone_number_id: embeddedSignupResult.phone_number_id,
        }),
        ...(embeddedSignupResult.waba_id && { business_account_id: embeddedSignupResult.waba_id }),
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      let errorMessage = `HTTP ${response.status}`
      if (errorData.error) {
        errorMessage = errorData.error
        if (errorData.error_data?.error?.message) {
          errorMessage = errorData.error_data.error.message
        }
      } else if (errorData.detail) {
        errorMessage = errorData.detail
      }
      throw new Error(errorMessage)
    }

    return response.json() as Promise<WhatsAppConnectionStatus>
  }

  const handleConnect = async () => {
    try {
      setLoading(true)
      setError(null)

      const shouldUseEmbeddedSignup =
        WHATSAPP_USE_EMBEDDED_SIGNUP && Boolean(WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID)

      if (shouldUseEmbeddedSignup) {
        const connectStatus = await connectWithEmbeddedSignup()
        syncWhatsAppStatus(connectStatus)
      } else {
        await connectWithLegacyFlow()
        const status = await whatsappService.getStatus()
        syncWhatsAppStatus(status)
      }
      conversationService.clearCache()
      notifyChannelConnectionChanged({ channel: 'whatsapp', connected: true })
    } catch (err) {
      console.error('Error connecting to WhatsApp:', err)
      if (err instanceof FacebookOAuthError) {
        if (err.message.includes('cancelled') || err.message.includes('did not authorize')) {
          setError(null)
        } else {
          setError(err.message)
        }
      } else {
        // Check if it's an Error object with a message
        const errorMessage = err instanceof Error ? err.message : String(err)
        
        setError(
          err instanceof WhatsAppServiceError
            ? err.message
            : errorMessage || getTranslation('settings.whatsapp.error_connecting') || 'Failed to connect to WhatsApp'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      setLoading(true)
      setError(null)

      await whatsappService.disconnect()
      syncWhatsAppStatus({ connected: false })
      conversationService.clearCache()
      notifyChannelConnectionChanged({ channel: 'whatsapp', connected: false })
    } catch (err) {
      console.error('Error disconnecting from WhatsApp:', err)
      setError(err instanceof WhatsAppServiceError ? err.message : getTranslation('settings.whatsapp.error_disconnecting'))
    } finally {
      setLoading(false)
    }
  }

  const isSetupIncomplete = isConnected && !isReady
  const statusTone = isReady
    ? 'bg-success/10 text-success'
    : permissionPending
      ? 'bg-warning/10 text-warning'
    : isConnected
      ? 'bg-warning/10 text-warning'
      : 'bg-muted text-muted-foreground'
  const statusLabel = isReady
    ? getTranslation('settings.whatsapp.ready_status')
    : permissionPending
      ? getTranslation('settings.whatsapp.permission_pending_status')
    : isConnected
      ? getTranslation('settings.whatsapp.setup_status')
      : getTranslation('settings.whatsapp.disconnected_status')

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">{getTranslation('settings.whatsapp.loading')}</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 flex-shrink-0 relative z-10">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${statusTone}`}>
            {isReady ? (
              <>
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{statusLabel}</span>
              </>
            ) : permissionPending ? (
              <>
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{statusLabel}</span>
              </>
            ) : isConnected ? (
              <>
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{statusLabel}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{statusLabel}</span>
              </>
            )}
          </div>
        </div>

        {/* Connect/Disconnect Button */}
        {/* Los numeros de la cuenta se dan de alta solos al conectar. Esto es para
            cuando Meta tiene uno nuevo que aqui todavia no aparece: vuelve a
            preguntarle, y los que ya estan se quedan como estan. */}
        {isConnected && (
          <Button
            variant="secondary"
            size="default"
            onClick={handleConnect}
            disabled={loading}
            className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            <span>Buscar números nuevos</span>
          </Button>
        )}
        {isConnected ? (
          <Button
            variant="outline"
            size="default"
            onClick={handleDisconnect}
            disabled={loading}
            className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{getTranslation('settings.whatsapp.loading')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                <span>{getTranslation('settings.whatsapp.disconnect_button')}</span>
              </>
            )}
          </Button>
        ) : (
          <Button
            variant="default"
            size="default"
            onClick={handleConnect}
            disabled={loading}
            className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{getTranslation('settings.whatsapp.loading')}</span>
              </>
            ) : (
              <>
                <MessageCircle className="h-4 w-4" />
                <span>{getTranslation('settings.whatsapp.connect_button')}</span>
              </>
            )}
          </Button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Embedded Signup ok, Meta App Review permission still required */}
      {isConnected && permissionPending && (
        <Alert className="border-warning/50 bg-warning/10 text-warning [&>svg]:text-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="text-sm font-semibold">
            {getTranslation("settings.whatsapp.permission_pending_title")}
          </AlertTitle>
          <AlertDescription className="space-y-2 text-xs">
            <p>{getTranslation("settings.whatsapp.permission_pending_body")}</p>
            <div className="rounded-md border border-warning/30 bg-background/70 p-3 font-mono text-[11px] leading-relaxed text-foreground">
              {getTranslation("settings.whatsapp.permission_pending_reviewer_note")}
            </div>
            {(connectionStatus?.account_id || connectionStatus?.phone_number_id) && (
              <div className="grid gap-1 text-muted-foreground">
                {connectionStatus.account_id && (
                  <p>
                    <span className="font-medium text-foreground">WABA ID:</span>{" "}
                    {connectionStatus.account_id}
                  </p>
                )}
                {connectionStatus.phone_number_id && (
                  <p>
                    <span className="font-medium text-foreground">Phone number ID:</span>{" "}
                    {connectionStatus.phone_number_id}
                  </p>
                )}
              </div>
            )}
            <p className="text-muted-foreground">
              {getTranslation("settings.whatsapp.permission_pending_action")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2 mt-1"
              onClick={handleRetryRegistration}
              disabled={retryLoading}
            >
              {retryLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              {getTranslation("settings.whatsapp.retry_registration")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Phone registration in progress (Cloud API /register queued) */}
      {isConnected && registrationPending && !permissionPending && (
        <Alert className="border-primary/40 bg-primary/5">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <AlertTitle className="text-sm">{getTranslation("settings.whatsapp.registration_pending_title")}</AlertTitle>
          <AlertDescription className="text-xs text-muted-foreground">
            {getTranslation("settings.whatsapp.registration_pending_body")}
          </AlertDescription>
        </Alert>
      )}

      {/* Phone registration failed — user can retry */}
      {isConnected && registrationFailed && (
        <Alert className="border-warning/40 bg-warning/10 text-warning [&>svg]:text-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="text-sm">{getTranslation("settings.whatsapp.registration_failed_title")}</AlertTitle>
          <AlertDescription className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              {typeof connectionStatus?.metadata?.phone_registration_error === "string"
                ? connectionStatus.metadata.phone_registration_error
                : getTranslation("settings.whatsapp.registration_failed_body")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2 mt-1"
              onClick={handleRetryRegistration}
              disabled={retryLoading}
            >
              {retryLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              {getTranslation("settings.whatsapp.retry_registration")}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isConnected && !registrationPending && !registrationFailed && webhookSubscriptionFailed && (
        <Alert className="border-warning/40 bg-warning/10 text-warning [&>svg]:text-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="text-sm">{getTranslation("settings.whatsapp.webhook_failed_title")}</AlertTitle>
          <AlertDescription className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              {typeof connectionStatus?.metadata?.webhook_subscription_error === "string"
                ? connectionStatus.metadata.webhook_subscription_error
                : getTranslation("settings.whatsapp.webhook_failed_body")}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {isSetupIncomplete && !registrationPending && !permissionPending && !registrationFailed && !webhookSubscriptionFailed && (
        <Alert className="border-primary/40 bg-primary/5">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <AlertTitle className="text-sm">{getTranslation("settings.whatsapp.setup_pending_title")}</AlertTitle>
          <AlertDescription className="text-xs text-muted-foreground">
            {getTranslation("settings.whatsapp.setup_pending_body")}
          </AlertDescription>
        </Alert>
      )}

      {/* Connection Info */}
      {isConnected && connectionStatus && (
        <>
          <Separator />
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{getTranslation('settings.whatsapp.connection_info')}</h3>

            {connectionStatus.phone_number_id && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.whatsapp.phone_number_id')}
                </Label>
                <p className="text-sm font-mono text-muted-foreground mt-1">{connectionStatus.phone_number_id}</p>
              </div>
            )}

            {connectionStatus.metadata?.display_phone_number && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.whatsapp.display_phone')}
                </Label>
                <p className="text-sm mt-1">{connectionStatus.metadata.display_phone_number}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Disconnected State Info */}
      {!isConnected && !loading && (
        <div className="p-4 rounded-md bg-muted/50 border border-dashed">
          <p className="text-sm text-muted-foreground text-center">
            {getTranslation('settings.whatsapp.description')}
          </p>
        </div>
      )}
    </div>
  )
}
